<template>
    <div class="email-info" v-loading="loading">
        <div class="email-action">
            <i class="md-icon">email</i>
            <div class="subject">{{ emailSubject }}</div>
            <el-button v-if="organisation && person" :type="emailInfo ? 'plain' : 'primary'" :loading="saving" size="small" @click="save" :disabled="!!emailInfo"><i class="md-icon" v-if="!saving">done</i>{{ emailInfo ? $t('saved') : $t('save') }}</el-button>
        </div>

        <organisation-info v-if="organisation" v-model="organisation" :organisations="organisations" />
        <div v-else-if="!loading" class="create-panel">
            <el-dropdown trigger="click" placement="bottom-start" @command="createOrganisation">
                <el-button type="secondary" @click="createOrganisation()" :disabled="!createOrganisationAllowed"><i class="md-icon">add</i>{{ $t('create') }} {{ $t('terms.organisation') }}</el-button>
                <el-dropdown-menu slot="dropdown" v-if="emailDomains.length > 1">
                    <el-dropdown-item v-for="domain in emailDomains" :key="domain" :command="domain">{{ domain }}</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </div>

        <person-info v-if="person" v-model="person" :persons="persons" />
        <div v-else-if="!loading" class="create-panel">
            <el-dropdown trigger="click" placement="bottom-start" @command="createPerson">
                <el-button type="secondary" @click="createPerson()" :disabled="!createPersonAllowed"><i class="md-icon">add</i>{{ $t('create') }} {{ $t('terms.person') }}</el-button>
                <el-dropdown-menu slot="dropdown" v-if="emailParties.length > 1">
                    <el-dropdown-item v-for="party in emailParties" :key="party.emailAddress" :command="party">{{ party.displayName || party.emailAddress }}</el-dropdown-item>
                </el-dropdown-menu>
            </el-dropdown>
        </div>

        <div class="entity-panels" v-if="!loading && organisation && entityTabs.length > 0" v-loading="!subEntityAccessData">
            <tabs :tabs="entityTabs" v-model="panelEntityType" :show-create="organisation && person && emailInfo" @create-entity="createEntity" />
            <value-switch v-if="readAllSubEntitiesAllowed" v-model="showAllSubEntities" :options="subEntitiesAccessOptions" class="sub-entities-switch" size="mini" />
            <entity-panel v-if="panelEntityType && subEntityAccessData" :entityType="panelEntityType" :organisationId="organisation.OrganisationId" :show-all="readAllSubEntitiesAllowed && showAllSubEntities" :email="emailInfo" />
        </div>
    </div>
</template>

<script lang="ts">
    import Vue from 'vue';

    import organisationInfo from '@/controls/entity/organisation-info.vue';
    import personInfo from '@/controls/entity/person-info.vue';
    import tabs from '@/controls/tabs.vue';
    import valueSwitch from '@/controls/valueSwitch.vue';
    import entityPanel from '@/controls/entity/entity-panel.vue';

    import { Person, PersonProvider } from '@/utilities/api/persons';
    import { Organisation, OrganisationProvider, OrganisationSubEntityAccessData, OrganisationSubEntityFullAccessData, OrganisationSubEntityNoAccessData } from '@/utilities/api/organisations';
    import { AttachmentType, Email, EmailInfo, EmailParty, EmailProvider, EmailType, OutlookEmailAttachmentData } from '@/utilities/api/email';
    import { Context } from '@/utilities/contex';
    import { EntityType } from '@/utilities/fields/metadata';
    import { NavigationProvider, Tab } from '@/utilities/navigation';
    import { EntityProvider } from '@/utilities/entities';
    import EventBus from '@/utilities/event-bus';

    const CategoryName = 'webCRM';

    export default Vue.extend({
        name: 'email-info',
        components: {
            organisationInfo,
            personInfo,
            tabs,
            valueSwitch,
            entityPanel,
        },
        data() {
            return {
                loading: true,
                saving: false,
                isOutbound: false,
                persons: [] as Person[],
                person: null as Person | null,
                organisations: [] as Organisation[],
                organisation: null as Organisation | null,
                emailInfo: null as EmailInfo | null,
                panelEntityType: EntityType.Undefined,
                subEntityAccessData: null as OrganisationSubEntityAccessData | null,
                showAllSubEntities: Context.config.showAllSubEntities,
                mailboxItem: null as Office.Item & Office.ItemRead & Office.Message & Office.MessageRead | null,
            }
        },
        async created() {
            EventBus.$on(`Office.${Office.EventType.ItemChanged}`, this.loadInfo);
            await this.loadInfo();
        },
        destroyed() {
            EventBus.$off(`Office.${Office.EventType.ItemChanged}`, this.loadInfo);
        },
        computed: {
            emailType(): EmailType {
                return this.isOutbound ? EmailType.Outbound : EmailType.Inbound;
            },
            emailExternalId(): string {
                return this.mailboxItem?.internetMessageId || this.mailboxItem?.itemId || '';
            },
            emailSubject(): string {
                return this.mailboxItem?.subject || '';
            },
            emailParties(): Office.EmailAddressDetails[] {
                if (!this.mailboxItem) return [];
                return this.isOutbound ? [...this.mailboxItem.to, ...this.mailboxItem.cc] : [this.mailboxItem.from];
            },
            emailAddresses(): string[] {
                return this.emailParties.map(p => p.emailAddress.toLowerCase());
            },
            emailDomains(): string[] {
                return [...new Set(this.emailAddresses
                    .map(e => e.split('@')[1])
                    .filter(e => !e.match(/^(gmail|hotmail|mail|live|outlook|yahoo|icloud|msn)\.(com|co.uk|\w{2})$/)))];
            },
            overwriteOrganisationId: {
                get(): number { return +this.$route.query.organisationId || 0; },
                set(value: number) { this.$router.push({ query: { organisationId: value ? value.toString() : undefined } }); }
            },
            overwritePersonId: {
                get(): number { return +this.$route.query.personId || 0; },
                set(value: number) { this.$router.push({ query: { personId: value ? value.toString() : undefined } }); },
            },
            createPersonAllowed(): boolean {
                return Context.user.accessSettings.createEntity;
            },
            createOrganisationAllowed(): boolean {
                return Context.user.accessSettings.createOrganisation;
            },
            readAllSubEntitiesAllowed(): boolean {
                return EntityProvider.canReadAllSubEntities(this.panelEntityType, this.subEntityAccessData);
            },
            subEntitiesAccessOptions(): any[] {
                return [
                    { value: true, label: this.$tc('all') },
                    { value: false, label: this.$tc('mine') },
                ];
            },
            entityTabs(): Tab<EntityType>[] {
                return NavigationProvider.getEntityTabs();
            },
        },
        watch: {
            'person.PersonOrganisationId'() {
                if (!this.person || this.organisation?.OrganisationId == this.person?.PersonOrganisationId) return;
                this.organisation = this.organisations.find(o => o.OrganisationId == this.person?.PersonOrganisationId) || null;
            },
            'organisation.OrganisationId'() {
                this.loadSubEntityAccessData();
                if (!this.organisation || this.person?.PersonOrganisationId == this.organisation?.OrganisationId) return;
                this.person = this.persons.find(p => p.PersonOrganisationId == this.organisation?.OrganisationId) || null;
            },
            panelEntityType() {
                Context.config.panelEntityType = this.panelEntityType;
            },
            showAllSubEntities() {
                Context.config.showAllSubEntities = this.showAllSubEntities;
            },
        },
        methods: {
            async loadInfo() {
                this.loading = true;

                this.resetEntities();
                this.mailboxItem = Office.context.mailbox.item!;

                await this.loadEmailType();

                await Promise.all([
                    this.loadEmail(),
                    this.loadPersonsAndOrganisations(),
                ]);

                this.selectLinkedPerson();

                this.loading = false;
            },
            resetEntities() {
                this.organisations = [];
                this.organisation = null;
                this.persons = [];
                this.person = null;
            },
            selectLinkedPerson() {
                if (this.emailInfo?.personId) {
                    const person = this.persons.find(p => p.PersonId == this.emailInfo?.personId);
                    if (person && person != this.person) {
                        this.person = person;
                    }
                }
            },
            async loadEmailType() {
                const headers = await new Promise(r => this.mailboxItem!.getAllInternetHeadersAsync(h => r(h.value))) as string;
                this.isOutbound = !headers.includes('Authentication-Results:');
            },
            async loadEmail() {
                this.emailInfo = await EmailProvider.getByExternalId(this.emailType, this.emailExternalId);
                this.panelEntityType = (
                    this.entityTabs.find(t => t.value == this.emailInfo?.entityType) ||
                    this.entityTabs.find(t => t.value == Context.config.panelEntityType) ||
                    this.entityTabs[0]
                )?.value;
            },
            async loadPersonsAndOrganisations() {
                if (this.overwritePersonId) {
                    await this.loadPerson();
                    await this.loadOrganisation(this.person?.PersonOrganisationId);
                } else if (this.overwriteOrganisationId) {
                    await this.loadOrganisation();
                } else {
                    await this.loadPersons();
                    await this.loadOrganisations();
                }
            },
            async loadPersons() {
                if (this.emailAddresses.length == 0) {
                    this.persons = [];
                    return;
                }
                this.persons = await PersonProvider.matchByEmails(this.emailAddresses);
                this.persons = this.emailAddresses.map(e => this.persons.filter(p => p.PersonEmail.toLowerCase() == e || p.PersonEmail2.toLowerCase() == e)).flat();
            },
            async loadOrganisations() {
                if (this.persons.length > 0) {
                    const organisationIds = [...new Set(this.persons.map(p => p.PersonOrganisationId))];
                    this.organisations = await OrganisationProvider.getbyIds(organisationIds);
                    this.organisations = organisationIds.map(id => this.organisations.find(o => o.OrganisationId == id)!).filter(o => o);
                    this.person = this.persons[0] || null;
                } else {
                    if (this.emailDomains.length == 0) {
                        this.organisations = [];
                        this.organisation = null;
                        return;
                    }
                    this.organisations = await OrganisationProvider.matchByDomains(this.emailDomains);
                    this.organisation = this.organisations[0] || null;
                }
            },
            async loadOrganisation(id?: number) {
                this.organisation = await OrganisationProvider.get(id || this.overwriteOrganisationId);
                this.organisations = [this.organisation];
                this.overwriteOrganisationId = 0;
            },
            async loadPerson(id?: number) {
                this.person = await PersonProvider.get(id || this.overwritePersonId);
                this.persons = [this.person];
                this.overwritePersonId = 0;
            },
            async loadSubEntityAccessData() {
                if (!this.organisation) {
                    this.subEntityAccessData = OrganisationSubEntityNoAccessData;
                    return;
                }
                if (Context.user.accessSettings.accessLevel == 99) {
                    this.subEntityAccessData = OrganisationSubEntityFullAccessData;
                    return;
                }

                this.subEntityAccessData = null; // Do not load entities yet, access not known
                this.subEntityAccessData = await OrganisationProvider.getSubEntityAccessData(this.organisation.OrganisationId);
            },
            async createOrganisation(domain?: string) {
                if (!domain && this.emailDomains.length > 1) return;
                if (!domain && this.emailDomains.length == 1) domain = this.emailDomains[0];
                this.$router.push({ path: '/outlook/organisation/create', query: {
                    domain,
                    returnTo: this.$route.fullPath
                }});
            },
            async createPerson(party?: Office.EmailAddressDetails) {
                if (!party && this.emailParties.length > 1) return;
                if (!party && this.emailParties.length == 1) party = this.emailParties[0];
                this.$router.push({ path: '/outlook/person/create', query: {
                    name: party?.displayName == party?.emailAddress ? '' : party?.displayName,
                    email: party?.emailAddress,
                    organisationId: (this.organisation?.OrganisationId || 0).toString(),
                    returnTo: this.$route.fullPath
                }});
            },
            async createEntity(entityType: EntityType) {
                this.$router.push({ path: '/outlook/entity/create', query: {
                    organisationId: (this.organisation?.OrganisationId || 0).toString(),
                    personId: (this.person?.PersonId || 0).toString(),
                    entityType: entityType.toString(),
                    emailType: (this.emailInfo?.type || EmailType.Outbound).toString(),
                    emailId: (this.emailInfo?.id || 0).toString(),
                    returnTo: this.$route.fullPath
                }});
            },
            async save() {
                this.saving = true;

                try {
                    const attachments = this.mailboxItem!.attachments.filter(a => a.attachmentType == Office.MailboxEnums.AttachmentType.File);
                    const attachmentsData = [];

                    if (attachments.length > 0) {
                        try {
                            const emlData = await new Promise((r, e) => this.mailboxItem!.getAsFileAsync(t => r(t.value || e(t) || ''))) as string;
                            attachmentsData.push({
                                name: 'email.eml',
                                type: AttachmentType.EmlCombinedAttachment,
                                data: emlData
                            });
                        }
                        catch (e: any) {
                            console.error(e);
                            // Outlook for Mac and older Windows Desktop clients
                            const inlineAttachemnts = attachments.filter(a => a.isInline);
                            const regularAttachments = attachments.filter(a => !a.isInline);

                            if (inlineAttachemnts.length > 0) {
                                try {
                                    // Need ContentID to embed images in the email body, not available here, use EWS to get from the backend
                                    const accessToken = await new Promise((r, e) => Office.context.mailbox.getCallbackTokenAsync(t => r(t.value || e(t) || ''))) as string;
                                    attachmentsData.push(...inlineAttachemnts.map(a => ({
                                        name: a.name,
                                        type: AttachmentType.OutlookEmailAttachment,
                                        data: JSON.stringify({ attachmentId: a.id, accessToken } as OutlookEmailAttachmentData)
                                    })));
                                } catch (e: any) {
                                    console.error(e);
                                    // Cannot get EWS access token, treat inline images as regular attachments
                                    regularAttachments.push(...inlineAttachemnts);
                                }
                            }

                            attachmentsData.push(...await Promise.all(regularAttachments.map(async a => ({
                                name: a.name,
                                type: AttachmentType.Base64EncodedAttachment,
                                data: await new Promise(r => this.mailboxItem!.getAttachmentContentAsync(a.id, c => r(c.value?.content || ''))) as string
                            }))));
                        }
                    }

                    const email = {
                        externalId: this.emailExternalId,
                        timestamp: this.mailboxItem!.dateTimeCreated.toISOString(),
                        type: this.emailType,
                        entityType: EntityType.Undefined,
                        entityId: 0,
                        subject: this.mailboxItem!.subject || '',
                        body: await new Promise(r => this.mailboxItem!.body.getAsync(Office.CoercionType.Html, b => r(b.value))),
                        from: this.mapEmailParty(this.mailboxItem!.from),
                        to: this.mailboxItem!.to.map(e => this.mapEmailParty(e)),
                        cc: this.mailboxItem!.cc.map(e => this.mapEmailParty(e)),
                        attachments: attachmentsData,
                        personId: this.person?.PersonId || 0,
                        userId: Context.user.userId,
                    } as Email;

                    email.id = await EmailProvider.save(email);
                    this.emailInfo = email;
                    await this.saveEmailCategory();

                } catch (e: any) {
                    console.error(e);
                    this.$notify.error({ title: this.$tc('error'), message: e.error || this.$tc('an_unexpected_error_occurred') });
                }

                this.saving = false;
            },
            async saveEmailCategory() {
                try {
                    await new Promise(r => this.mailboxItem!.categories.addAsync([CategoryName], r));
                } catch {
                    // Ignore errors. If category doesn't exist, the user doesn't want to mark the email
                }
            },
            mapEmailParty(details: Office.EmailAddressDetails): EmailParty {
                return {
                    name: details.displayName || details.emailAddress,
                    email: details.emailAddress,
                };
            }
        }
    });

</script>

<style scoped lang="scss">

    .email-info {
        min-height: 300px;

        & > *:not(:first-child) {
            margin-top: 12px;
            padding-top: 12px;
            border-top: solid 1px var(--grey-200);
        }
    }

    .email-action {
        display: flex;
        align-items: center;
        gap: 8px;

        & > .md-icon {
            font-size: 24px;
            flex-shrink: 0;
        }

        .subject {
            @extend %headline-h3;
            @extend %no-overflow;
            flex-grow: 1;
        }
    }

    .create-panel {
        .el-dropdown, .el-button {
            width: 100%;
        }
    }

    .entity-panels {
        .tabs-container {
            position: sticky;
            background: var(--white);
            top: 0;
            margin: -8px 0;
            padding: 8px 0;
            z-index: 3;
        }

        .sub-entities-switch {
            margin-top: 12px;
        }

        .entity-panel {
            margin-top: 12px;
        }
    }

</style>